/**
 * Qué se puede hacer con un documento según dónde se haya quedado.
 *
 * Las dos preguntas viven juntas porque son la misma mirada desde dos lados:
 * **¿llegó este documento a Business Central?**
 *
 * - Si NO llegó, todavía es nuestro: se puede corregir.
 * - Si SÍ llegó, ya no: lo que falta es registrarlo, y corregirlo sería
 *   contradecir algo que del otro lado ya existe.
 *
 * Tenerlas separadas fue justo lo que dejó pasar el defecto: el botón de
 * contabilizar miraba `pendingSync`, y en cuanto un fallo dejó de reintentarse
 * --los datos que Hacienda rechazaría no se reintentan, porque volver a
 * mandarlos daría el mismo rechazo-- ese campo quedó en cero y el botón se
 * encendió sobre un documento que **nunca había salido del terminal**. Pulsarlo
 * lo daba de alta con los datos malos, que es exactamente lo que se quería
 * evitar.
 */

const texto = (valor) => String(valor ?? "").trim();

/**
 * Si el documento existe ya en Business Central.
 *
 * El número preasignado es la prueba: lo devuelve BC al crear la cabecera. El
 * `systemId` vale igual, y `No` es el definitivo, que sólo aparece al
 * contabilizar.
 */
export const estaEnBusinessCentral = (documento) =>
  Boolean(
    texto(documento?.Pre_Assigned_No) || texto(documento?.No) || texto(documento?.systemId)
  );

/**
 * Si se puede editar.
 *
 * Un borrador, siempre: es lo de toda la vida. Y además —esto es lo nuevo— un
 * documento que **falló antes de salir**: si los datos no pasaron la validación,
 * el documento se quedó entero en el terminal y no hay nada emitido que
 * contradecir. Sin esto, al cajero sólo le quedaban sincronizar --que vuelve a
 * fallar por lo mismo-- y borrar.
 *
 * Se exige que no esté pendiente de envío: la sincronización de fondo puede
 * tomarlo en cualquier momento, y editar algo que está saliendo es la forma de
 * mandar la mitad de una cosa y la mitad de otra.
 */
export const sePuedeEditar = (documento) => {
  if (!documento || documento.deleted) return false;
  if (documento.isDraft) return true;
  if (documento.pendingSync) return false;
  return !estaEnBusinessCentral(documento);
};

/** Por qué no se puede editar, para decírselo a quien pulsa. */
export const porQueNoSePuedeEditar = (documento) => {
  if (sePuedeEditar(documento)) return "";
  if (documento?.deleted) return "Este documento está eliminado";
  if (documento?.pendingSync) return "Se está enviando a Business Central: espera a que termine";
  if (estaEnBusinessCentral(documento)) {
    return "Ya existe en Business Central y no se puede modificar; corrígelo con una nota de crédito";
  }
  return "Solo se pueden editar borradores y documentos que no llegaron a enviarse";
};

/**
 * Si se puede reanudar el registro.
 *
 * Sólo cuando el documento **ya está en Business Central** y no llegó a
 * contabilizarse. Ése es el hueco que este botón cubre: la venta se envió
 * entera y el registro falló.
 *
 * Un documento que no salió del terminal no tiene nada que reanudar, y encender
 * el botón ahí lo daría de alta saltándose la validación.
 */
export const sePuedeContabilizar = (documento) => {
  if (!documento || documento.deleted || documento.isDraft) return false;
  // Ya contabilizado: no hay nada que hacer.
  if (documento.posted) return false;
  // Falta enviarlo, o el envío falló: primero se sincroniza.
  if (documento.pendingSync || documento.syncError) return false;
  return estaEnBusinessCentral(documento);
};

/** Por qué no se puede contabilizar. */
export const porQueNoSePuedeContabilizar = (documento) => {
  if (sePuedeContabilizar(documento)) return "Contabilizar en Business Central";
  if (documento?.posted) return "Ya está contabilizado";
  if (documento?.isDraft) return "Un borrador todavía no se puede contabilizar";
  if (documento?.pendingSync) return "Se está enviando: espera a que termine";
  if (documento?.syncError) return "No se pudo enviar a Business Central: sincronízalo primero";
  if (!estaEnBusinessCentral(documento)) {
    return "Todavía no está en Business Central: sincronízalo primero";
  }
  return "No se puede contabilizar";
};
